import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';

// Cache for generated top-down 2D image snapshots
// Key: `${fileUrl}:${targetHeight}`
// Value: { status: 'loading'|'ready'|'error', image: HTMLCanvasElement|HTMLImageElement, realWidthM, realLengthM, aspect }
const snapshotCache = new Map();
const pendingCallbacks = new Map();

// Helper to determine model classification from configuration
export const detectModelType = (modelConfig) => {
  const catRaw = (modelConfig?.category || '').toLowerCase();
  const nameLower = (modelConfig?.modelName || modelConfig?.name || '').toLowerCase();
  const fileLower = (modelConfig?.fileUrl || modelConfig?.file_url || '').toLowerCase();

  // 1. Power Pallet Truck / Walkie / Ride-on (e.g. Toyota BT Levio LPE200)
  if (
    nameLower.includes('power pallet') ||
    nameLower.includes('pallet truck') ||
    nameLower.includes('lpe') ||
    nameLower.includes('tiller') ||
    nameLower.includes('walkie') ||
    fileLower.includes('power_pallet') ||
    fileLower.includes('lpe200')
  ) {
    return 'power_pallet';
  }

  // 2. Pallet Station / Floor Guides (Pallet guide rails & stoppers with pallet)
  if (
    nameLower.includes('floor guide') ||
    nameLower.includes('pallet station') ||
    nameLower.includes('stoppers') ||
    fileLower.includes('floor_guide') ||
    fileLower.includes('pallet_station')
  ) {
    return 'pallet_station';
  }

  // 3. Inbound Station / Overhead Vision Scanner / RFID Gantry
  if (
    nameLower.includes('inbound') ||
    nameLower.includes('visionary') ||
    nameLower.includes('scanner') ||
    nameLower.includes('gantry') ||
    nameLower.includes('twin mast') ||
    fileLower.includes('visionary') ||
    fileLower.includes('twin_mast') ||
    fileLower.includes('inbound')
  ) {
    return 'inbound_station';
  }

  // 4. Pallet Dimensional / Profile Checker Gate
  if (
    nameLower.includes('pallet checker') ||
    nameLower.includes('profile checker') ||
    fileLower.includes('pallet_profile_checker')
  ) {
    return 'pallet_checker';
  }

  // 5. Operator / Human Worker
  if (
    catRaw === 'operator' ||
    nameLower.includes('operator') ||
    nameLower.includes('worker') ||
    nameLower.includes('human') ||
    nameLower.includes('person')
  ) {
    return 'operator';
  }

  // 6. Reach Truck (e.g. Toyota RAE, RRE)
  if (
    catRaw === 'reach_truck' ||
    nameLower.includes('reach') ||
    nameLower.includes('rae') ||
    nameLower.includes('rre') ||
    fileLower.includes('reach')
  ) {
    return 'reach_truck';
  }

  // 7. Counterbalance Forklift
  if (
    catRaw === 'forklift' ||
    nameLower.includes('forklift') ||
    nameLower.includes('fl') ||
    fileLower.includes('forklift')
  ) {
    return 'forklift';
  }

  // 8. Desktop Workstation / Computer & Barcode Printer
  if (
    nameLower.includes('computer') ||
    nameLower.includes('desktop') ||
    nameLower.includes('workstation')
  ) {
    return 'desktop_workstation';
  }

  // 9. Equipment / Machinery / Conveyor
  if (
    catRaw === 'equipment' ||
    nameLower.includes('machine') ||
    nameLower.includes('conveyor') ||
    nameLower.includes('robot')
  ) {
    return 'equipment';
  }

  return catRaw || 'other';
};

/**
 * Request or retrieve a top-down orthographic snapshot of a 3D model (.glb)
 * Returns the cached snapshot object if ready, or null if loading.
 * Calls onReadyCallback when the snapshot finishes rendering so the canvas can repaint.
 */
export const getOrRequestModelSnapshot = (fileUrl, targetHeight = 1.70, onReadyCallback = null) => {
  if (!fileUrl) return null;

  const cacheKey = `${fileUrl}:${(targetHeight || 1.7).toFixed(2)}`;

  if (snapshotCache.has(cacheKey)) {
    const entry = snapshotCache.get(cacheKey);
    if (entry.status === 'ready') {
      return entry;
    }
    if (entry.status === 'loading' && onReadyCallback) {
      if (!pendingCallbacks.has(cacheKey)) pendingCallbacks.set(cacheKey, []);
      pendingCallbacks.get(cacheKey).push(onReadyCallback);
    }
    return null;
  }

  // Mark as loading
  snapshotCache.set(cacheKey, { status: 'loading' });
  if (onReadyCallback) {
    if (!pendingCallbacks.has(cacheKey)) pendingCallbacks.set(cacheKey, []);
    pendingCallbacks.get(cacheKey).push(onReadyCallback);
  }

  // Load and render with Three.js offscreen
  const loader = new GLTFLoader();
  loader.load(
    fileUrl,
    (gltf) => {
      try {
        const scene = gltf.scene;

        // Measure original bounding box
        const origBox = new THREE.Box3().setFromObject(scene);
        const origSize = new THREE.Vector3();
        origBox.getSize(origSize);

        const origH = origSize.y || 1;
        const scale = (targetHeight && targetHeight > 0.01) ? (targetHeight / origH) : 1;
        scene.scale.set(scale, scale, scale);

        // Recompute scaled dimensions
        const scaledBox = new THREE.Box3().setFromObject(scene);
        const scaledCenter = new THREE.Vector3();
        const scaledSize = new THREE.Vector3();
        scaledBox.getCenter(scaledCenter);
        scaledBox.getSize(scaledSize);

        const realWidthM = Math.max(0.1, scaledSize.x);
        const realLengthM = Math.max(0.1, scaledSize.z);

        // Center model at origin with bottom at Y=0
        scene.position.set(-scaledCenter.x, -scaledBox.min.y, -scaledCenter.z);

        // Offscreen canvas (high resolution, e.g. 512px)
        const maxRes = 512;
        const maxDim = Math.max(realWidthM, realLengthM, 0.1);
        const canvasW = Math.max(64, Math.round((realWidthM / maxDim) * maxRes));
        const canvasH = Math.max(64, Math.round((realLengthM / maxDim) * maxRes));

        const canvas = document.createElement('canvas');
        canvas.width = canvasW;
        canvas.height = canvasH;

        const renderer = new THREE.WebGLRenderer({
          canvas,
          alpha: true,
          antialias: true,
          preserveDrawingBuffer: true
        });
        renderer.setSize(canvasW, canvasH);
        renderer.setClearColor(0x000000, 0); // Transparent background
        if (THREE.SRGBColorSpace) {
          renderer.outputColorSpace = THREE.SRGBColorSpace;
        }

        const stage = new THREE.Scene();
        stage.add(scene);

        // High-clarity lighting for architectural top-down view
        const amb = new THREE.AmbientLight(0xffffff, 2.4);
        stage.add(amb);
        const d1 = new THREE.DirectionalLight(0xffffff, 1.8);
        d1.position.set(6, 25, 6);
        stage.add(d1);
        const d2 = new THREE.DirectionalLight(0xffffff, 1.2);
        d2.position.set(-6, 20, -6);
        stage.add(d2);

        // Top-Down Orthographic Camera:
        // Positioned directly above looking down (-Y)
        // camera.up = (0, 0, -1) so -Z (North) is at top of image and +Z (South/Front) is at bottom
        const halfW = realWidthM / 2;
        const halfL = realLengthM / 2;
        const camera = new THREE.OrthographicCamera(-halfW, halfW, halfL, -halfL, 0.1, 100);
        camera.position.set(0, 30, 0);
        camera.up.set(0, 0, -1);
        camera.lookAt(0, 0, 0);
        camera.updateProjectionMatrix();

        renderer.render(stage, camera);

        // Convert canvas to immutable Image element
        const img = new Image();
        img.onload = () => {
          snapshotCache.set(cacheKey, {
            status: 'ready',
            image: img,
            realWidthM,
            realLengthM,
            aspect: realWidthM / realLengthM
          });

          // Trigger callbacks to re-render map
          const cbs = pendingCallbacks.get(cacheKey) || [];
          pendingCallbacks.delete(cacheKey);
          cbs.forEach(cb => {
            try { cb(); } catch (e) { console.warn('Callback error:', e); }
          });
        };
        img.src = canvas.toDataURL('image/png');

        // Clean up renderer context
        renderer.dispose();
      } catch (err) {
        console.warn('Error generating top-down snapshot for 3D model:', fileUrl, err);
        snapshotCache.set(cacheKey, { status: 'error' });
      }
    },
    undefined,
    (err) => {
      console.warn('Failed to load 3D model GLB for top-down snapshot:', fileUrl, err);
      snapshotCache.set(cacheKey, { status: 'error' });
    }
  );

  return null;
};

// --------------------------------------------------------------------------
// Procedural 2D CAD Drawing Fallbacks (Instant rendering while GLB snapshot loads)
// --------------------------------------------------------------------------

/**
 * 1. Toyota BT Levio LPE200 Power Pallet Truck (Walkie / Ride-on)
 */
export const drawPowerPalletTruck = (ctx, truckW, totalLen, uiScale) => {
  const rearY = -totalLen / 2;
  const platformL = totalLen * 0.18;
  const chassisL = totalLen * 0.38;
  const forkL = totalLen * 0.44;
  const mastY = rearY + platformL + chassisL;
  const forkTipY = totalLen / 2;

  // 1. Foldable Operator Platform at Rear
  const platW = truckW * 0.88;
  ctx.fillStyle = '#1e293b';
  ctx.strokeStyle = '#0f172a';
  ctx.lineWidth = 1 * uiScale;
  if (ctx.roundRect) {
    ctx.beginPath();
    ctx.roundRect(-platW / 2, rearY, platW, platformL, 3 * uiScale);
    ctx.fill();
    ctx.stroke();
  } else {
    ctx.fillRect(-platW / 2, rearY, platW, platformL);
  }
  // Anti-slip diamond grip ribs on platform
  ctx.strokeStyle = '#475569';
  ctx.lineWidth = 0.8 * uiScale;
  for (let py = rearY + 3 * uiScale; py < rearY + platformL - 1; py += 3 * uiScale) {
    ctx.beginPath();
    ctx.moveTo(-platW / 2 + 3 * uiScale, py);
    ctx.lineTo(platW / 2 - 3 * uiScale, py);
    ctx.stroke();
  }

  // 2. Battery & Drive Unit Body (Toyota Industrial Orange)
  const bodyY = rearY + platformL;
  ctx.fillStyle = '#ea580c';
  ctx.strokeStyle = '#c2410c';
  ctx.lineWidth = 1.3 * uiScale;
  if (ctx.roundRect) {
    ctx.beginPath();
    ctx.roundRect(-truckW / 2, bodyY, truckW, chassisL, [4 * uiScale, 4 * uiScale, 2 * uiScale, 2 * uiScale]);
    ctx.fill();
    ctx.stroke();
  } else {
    ctx.fillRect(-truckW / 2, bodyY, truckW, chassisL);
  }

  // Battery compartment hatch
  ctx.fillStyle = '#0f172a';
  const hatchW = truckW * 0.72;
  const hatchL = chassisL * 0.45;
  if (ctx.roundRect) {
    ctx.beginPath();
    ctx.roundRect(-hatchW / 2, bodyY + chassisL * 0.12, hatchW, hatchL, 2 * uiScale);
    ctx.fill();
  } else {
    ctx.fillRect(-hatchW / 2, bodyY + chassisL * 0.12, hatchW, hatchL);
  }

  // 3. Tiller Arm & Ergonomic Steering Handle
  ctx.fillStyle = '#1e293b';
  ctx.fillRect(-2 * uiScale, bodyY + chassisL * 0.58, 4 * uiScale, chassisL * 0.32);
  // Tiller Butterfly Grips (dual wing handle)
  const tillerY = bodyY + chassisL * 0.85;
  ctx.fillStyle = '#0f172a';
  ctx.strokeStyle = '#334155';
  ctx.lineWidth = 1 * uiScale;
  ctx.beginPath();
  ctx.ellipse(0, tillerY, truckW * 0.28, 3.5 * uiScale, 0, 0, Math.PI * 2);
  ctx.fill();
  ctx.stroke();
  // Emergency Belly Button (Safety Stop)
  ctx.fillStyle = '#ef4444';
  ctx.beginPath();
  ctx.arc(0, tillerY - 1 * uiScale, 2 * uiScale, 0, Math.PI * 2);
  ctx.fill();

  // 4. Dual Steel Forks
  const forkW = Math.max(2 * uiScale, truckW * 0.23);
  const forkSpacing = truckW * 0.25;
  [-forkSpacing, forkSpacing].forEach(fx => {
    ctx.fillStyle = '#e2e8f0';
    ctx.strokeStyle = '#64748b';
    ctx.lineWidth = 1 * uiScale;
    ctx.beginPath();
    ctx.moveTo(fx - forkW / 2, mastY);
    ctx.lineTo(fx - forkW / 2, forkTipY - 3 * uiScale);
    ctx.lineTo(fx, forkTipY);
    ctx.lineTo(fx + forkW / 2, forkTipY - 3 * uiScale);
    ctx.lineTo(fx + forkW / 2, mastY);
    ctx.closePath();
    ctx.fill();
    ctx.stroke();

    // Tandem polyurethane load rollers at fork tips
    ctx.fillStyle = '#94a3b8';
    ctx.fillRect(fx - forkW * 0.32, forkTipY - 8 * uiScale, forkW * 0.64, 5 * uiScale);
  });

  // Direction indicator arrow at tip
  ctx.fillStyle = '#ef4444';
  ctx.beginPath();
  ctx.moveTo(0, forkTipY + 4 * uiScale);
  ctx.lineTo(-3.5 * uiScale, forkTipY - 1.5 * uiScale);
  ctx.lineTo(3.5 * uiScale, forkTipY - 1.5 * uiScale);
  ctx.closePath();
  ctx.fill();
};

/**
 * 2. Pallet Station with Floor Guides and Cargo Pallet
 */
export const drawPalletStationWithGuides = (ctx, w, l, uiScale) => {
  const halfW = w / 2;
  const halfL = l / 2;

  // 1. Steel Floor Guide Rails (Left & Right)
  const railW = Math.max(3 * uiScale, w * 0.08);
  const railL = l * 0.95;

  [-halfW + railW / 2, halfW - railW / 2].forEach(rx => {
    ctx.fillStyle = '#334155';
    ctx.strokeStyle = '#0f172a';
    ctx.lineWidth = 1 * uiScale;
    ctx.fillRect(rx - railW / 2, -railL / 2, railW, railL);
    ctx.strokeRect(rx - railW / 2, -railL / 2, railW, railL);

    // Hazard warning stripes (/ / / /)
    ctx.save();
    ctx.beginPath();
    ctx.rect(rx - railW / 2, -railL / 2, railW, railL);
    ctx.clip();
    ctx.lineWidth = 1.5 * uiScale;
    for (let sy = -railL / 2; sy < railL / 2 + 10; sy += 6 * uiScale) {
      ctx.strokeStyle = '#facc15';
      ctx.beginPath();
      ctx.moveTo(rx - railW, sy);
      ctx.lineTo(rx + railW, sy + 4 * uiScale);
      ctx.stroke();
    }
    ctx.restore();

    // Floor entry chamfer / guide funnel at entrance
    ctx.fillStyle = '#475569';
    ctx.beginPath();
    ctx.moveTo(rx - railW / 2, railL / 2);
    ctx.lineTo(rx + railW / 2, railL / 2);
    ctx.lineTo(rx + railW * 0.8, railL / 2 + 3 * uiScale);
    ctx.lineTo(rx - railW * 0.8, railL / 2 + 3 * uiScale);
    ctx.closePath();
    ctx.fill();
  });

  // 2. Center Pallet Deck (Wood)
  const palW = w * 0.76;
  const palL = l * 0.82;
  ctx.fillStyle = '#d97706';
  ctx.strokeStyle = '#b45309';
  ctx.lineWidth = 1 * uiScale;
  ctx.fillRect(-palW / 2, -palL / 2, palW, palL);
  ctx.strokeRect(-palW / 2, -palL / 2, palW, palL);

  // Pallet Slats
  ctx.strokeStyle = '#92400e';
  ctx.lineWidth = 0.8 * uiScale;
  for (let s = -palL / 2 + palL * 0.2; s < palL / 2; s += palL * 0.2) {
    ctx.beginPath();
    ctx.moveTo(-palW / 2, s);
    ctx.lineTo(palW / 2, s);
    ctx.stroke();
  }

  // 3. Yellow/Kraft Carton Cargo Box on Pallet
  const boxW = palW * 0.88;
  const boxL = palL * 0.88;
  ctx.fillStyle = '#fef3c7';
  ctx.strokeStyle = '#d97706';
  ctx.lineWidth = 1.2 * uiScale;
  if (ctx.roundRect) {
    ctx.beginPath();
    ctx.roundRect(-boxW / 2, -boxL / 2, boxW, boxL, 2 * uiScale);
    ctx.fill();
    ctx.stroke();
  } else {
    ctx.fillRect(-boxW / 2, -boxL / 2, boxW, boxL);
    ctx.strokeRect(-boxW / 2, -boxL / 2, boxW, boxL);
  }

  // Carton box strapping bands
  ctx.strokeStyle = '#f59e0b';
  ctx.lineWidth = 1.2 * uiScale;
  ctx.beginPath();
  ctx.moveTo(-boxW * 0.25, -boxL / 2); ctx.lineTo(-boxW * 0.25, boxL / 2);
  ctx.moveTo(boxW * 0.25, -boxL / 2);  ctx.lineTo(boxW * 0.25, boxL / 2);
  ctx.stroke();
};

/**
 * 3. Inbound Scanner Gantry (Twin Mast with Overhead Beam & Downward Camera)
 */
export const drawInboundScannerGantry = (ctx, w, l, uiScale) => {
  const halfW = w / 2;
  const halfL = l / 2;

  // 1. Dual Floor Baseplates and Masts (Left & Right)
  const plateSize = Math.max(8 * uiScale, Math.min(w * 0.22, 18 * uiScale));
  [-halfW + plateSize / 2 + 2 * uiScale, halfW - plateSize / 2 - 2 * uiScale].forEach(px => {
    // Floor mounting plate
    ctx.fillStyle = '#1e293b';
    ctx.strokeStyle = '#0f172a';
    ctx.lineWidth = 1 * uiScale;
    ctx.fillRect(px - plateSize / 2, -plateSize / 2, plateSize, plateSize);
    ctx.strokeRect(px - plateSize / 2, -plateSize / 2, plateSize, plateSize);

    // Anchor bolt studs (4 corners)
    ctx.fillStyle = '#cbd5e1';
    const boltOff = plateSize * 0.35;
    [-boltOff, boltOff].forEach(bx => {
      [-boltOff, boltOff].forEach(by => {
        ctx.beginPath();
        ctx.arc(px + bx, by, 1.2 * uiScale, 0, Math.PI * 2);
        ctx.fill();
      });
    });

    // Vertical Tubular Column (cross section)
    ctx.fillStyle = '#64748b';
    ctx.beginPath();
    ctx.arc(px, 0, plateSize * 0.28, 0, Math.PI * 2);
    ctx.fill();
    ctx.stroke();
  });

  // 2. Transverse Overhead Truss Beam connecting masts
  const beamY = -2 * uiScale;
  const beamThick = Math.max(3.5 * uiScale, l * 0.35);
  ctx.fillStyle = '#475569';
  ctx.strokeStyle = '#1e293b';
  ctx.lineWidth = 1.2 * uiScale;
  ctx.fillRect(-halfW + plateSize, beamY - beamThick / 2, w - plateSize * 2, beamThick);
  ctx.strokeRect(-halfW + plateSize, beamY - beamThick / 2, w - plateSize * 2, beamThick);

  // 3. Center Downward 3D Vision Sensor / SICK Camera Unit
  const sensorW = Math.max(10 * uiScale, w * 0.18);
  const sensorL = Math.max(7 * uiScale, l * 0.55);
  ctx.fillStyle = '#0284c7';
  ctx.strokeStyle = '#0369a1';
  ctx.lineWidth = 1.2 * uiScale;
  if (ctx.roundRect) {
    ctx.beginPath();
    ctx.roundRect(-sensorW / 2, -sensorL / 2, sensorW, sensorL, 2 * uiScale);
    ctx.fill();
    ctx.stroke();
  } else {
    ctx.fillRect(-sensorW / 2, -sensorL / 2, sensorW, sensorL);
  }

  // Camera lens aperture
  ctx.fillStyle = '#0f172a';
  ctx.beginPath();
  ctx.arc(0, 0, sensorL * 0.28, 0, Math.PI * 2);
  ctx.fill();
  ctx.fillStyle = '#38bdf8';
  ctx.beginPath();
  ctx.arc(0, 0, sensorL * 0.14, 0, Math.PI * 2);
  ctx.fill();

  // Status LED indicators (Green & Blue)
  ctx.fillStyle = '#22c55e';
  ctx.beginPath();
  ctx.arc(-sensorW * 0.3, 0, 1.4 * uiScale, 0, Math.PI * 2);
  ctx.fill();
  ctx.fillStyle = '#38bdf8';
  ctx.beginPath();
  ctx.arc(sensorW * 0.3, 0, 1.4 * uiScale, 0, Math.PI * 2);
  ctx.fill();

  // Laser scanning plane fan preview (translucent cyan)
  ctx.fillStyle = 'rgba(56, 189, 248, 0.15)';
  ctx.beginPath();
  ctx.moveTo(0, sensorL / 2);
  ctx.lineTo(-halfW * 0.7, halfL);
  ctx.lineTo(halfW * 0.7, halfL);
  ctx.closePath();
  ctx.fill();
};

/**
 * 4. Pallet Profile Checker / Inspection Gate
 */
export const drawPalletProfileChecker = (ctx, w, l, uiScale) => {
  const halfW = w / 2;
  const halfL = l / 2;

  // Dual side posts
  const postW = Math.max(4 * uiScale, w * 0.12);
  [-halfW + postW / 2, halfW - postW / 2].forEach(px => {
    ctx.fillStyle = '#0f172a';
    ctx.fillRect(px - postW / 2, -halfL, postW, l);
    // Optical light curtain sensors
    ctx.fillStyle = '#ef4444';
    for (let sy = -halfL + 4 * uiScale; sy < halfL; sy += 5 * uiScale) {
      ctx.beginPath();
      ctx.arc(px, sy, 1.2 * uiScale, 0, Math.PI * 2);
      ctx.fill();
    }
  });

  // Top arch frame
  ctx.fillStyle = '#1e293b';
  ctx.fillRect(-halfW, -halfL, w, Math.max(3 * uiScale, l * 0.25));

  // Light curtain beams
  ctx.strokeStyle = 'rgba(239, 68, 68, 0.25)';
  ctx.lineWidth = 1 * uiScale;
  ctx.beginPath();
  for (let sy = -halfL + 4 * uiScale; sy < halfL; sy += 6 * uiScale) {
    ctx.moveTo(-halfW + postW, sy);
    ctx.lineTo(halfW - postW, sy);
  }
  ctx.stroke();
};

/**
 * 5. Warehouse Desktop Workstation (Computer, Label Printer, Barcode Scanner)
 */
export const drawDesktopWorkstation = (ctx, w, l, uiScale) => {
  const halfW = w / 2;
  const halfL = l / 2;

  // Table surface (Light gray laminate with dark border)
  ctx.fillStyle = '#f1f5f9';
  ctx.strokeStyle = '#64748b';
  ctx.lineWidth = 1.2 * uiScale;
  if (ctx.roundRect) {
    ctx.beginPath();
    ctx.roundRect(-halfW, -halfL, w, l, 3 * uiScale);
    ctx.fill();
    ctx.stroke();
  } else {
    ctx.fillRect(-halfW, -halfL, w, l);
    ctx.strokeRect(-halfW, -halfL, w, l);
  }

  // Widescreen LCD Monitor
  const monW = w * 0.55;
  const monL = Math.max(2.5 * uiScale, l * 0.12);
  ctx.fillStyle = '#0f172a';
  ctx.fillRect(-monW / 2, -halfL * 0.45, monW, monL);
  // Monitor stand base
  ctx.fillRect(-monW * 0.15, -halfL * 0.55, monW * 0.30, monL * 0.8);

  // Keyboard
  const kbW = w * 0.42;
  const kbL = l * 0.25;
  ctx.fillStyle = '#334155';
  ctx.fillRect(-kbW / 2, halfL * 0.05, kbW, kbL);

  // Label printer
  const prntW = w * 0.22;
  const prntL = l * 0.32;
  ctx.fillStyle = '#1e293b';
  ctx.fillRect(halfW * 0.4, -halfL * 0.35, prntW, prntL);
};
